// Library imports from remote libraries.
import _ from 'lodash';
import moment from 'moment';

// Function imports from remote libraries.
import { putAsync } from 'js-csp';

// Local libraries.
import * as api from 'assets/js/api';
import CURRENCIES from 'static/json/currencies_sm.json';
import Debounce from 'assets/js/debounceasync';
import SSO from 'assets/js/auth/sso';
import auth from 'assets/js/auth';

import { ADD_TOAST_MESSAGE } from 'store/toast';
import { CI_TESTING, STATIC_URL } from 'assets/js/api/environment';
import { currencyStringToInt } from 'assets/js/currency';
import { EmailVerifierMsgs } from 'assets/js/errors';
import { SanitizeHTML } from 'assets/js/mixins/sanitize';
import { faIcons } from 'assets/js/mixins/icons';
import { uuid4 } from 'assets/js/util';

export const EmailDebounceTime = 300;

// From https://emailregex.com/
// eslint-disable-next-line
const emailRegex = /(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-zA-Z0-9-]*[a-zA-Z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;

// From https://stackoverflow.com/a/26987741
// eslint-disable-next-line
const domainRegex = /^((?!-))(xn--)?[a-z0-9][a-z0-9-_]{0,61}[a-z0-9]{0,1}\.(xn--)?([a-z0-9\-]{1,61}|[a-z0-9-]{1,30}\.[a-z]{2,})$/i;

// stripeInit initializes stripe in a browser window.
const stripeInit = (dcmnt, wndw) => {
  if (!wndw.Stripe) {
    const script = dcmnt.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://js.stripe.com/v3/';
    dcmnt.body.appendChild(script);
  }
};

const waitForRefToLoadDelay = 25;
const waitForRefToLoadMaxIterations = 100;

// AsyncForEach allows you to do a forEach loop asynchronously.
export const AsyncForEach = async (data, callback) => {
  let dataLocal = data;
  if (_.isPlainObject(dataLocal)) {
    dataLocal = Object.entries(data);
    for (let index = 0; index < dataLocal.length; index += 1) {
      const val = await callback(dataLocal[index][1], dataLocal[index][0],
        index, dataLocal);
      if (val === false) break;
    }
    return;
  }
  if (_.isArray(dataLocal)) {
    for (let index = 0; index < dataLocal.length; index += 1) {
      const val = await callback(dataLocal[index], index, dataLocal);
      if (val === false) break;
    }
  } else {
    throw Error(`AsyncForEach got bad data type ${typeof data}`);
  }
};

export const mixinMethods = (ctx) => ({
  // arrayToObject converts an array of elements to an object mapping elements
  // using a key and a value function.
  arrayToObject(arr, keyFn, valFn) {
    return _.fromPairs(
      _.map(arr, (ele, idx) => [
        keyFn(ele, idx),
        valFn(ele, idx),
      ]),
    );
  },

  // arrayToSentence converts an array of strings into a grammatically correct
  // sentence.
  arrayToSentence(arr) {
    const oxfordAnd = (len) => (len > 2 ? ', and ' : ' and ');
    if (arr.length === 1) {
      return arr[0];
    } else if (arr.length === 0) {
      return '';
    }
    const leftSide = arr.slice(0, arr.length - 1).join(', ');
    return leftSide + oxfordAnd(arr.length) + arr[arr.length - 1];
  },

  // arrayMToN creates an array of length N-M+1, with the integers filled in
  // as M ... N.
  arrayMToN(m, n) {
    if (m > n) return [];
    return _.range(m, n + 1);
  },

  // arrayOneToN creates an array of length n, with the integers filled in
  // as 1 ... n. The optional noShift parameter makes the index 0-ordered.
  arrayOneToN(n, noShift) {
    if (noShift) return _.range(0, n);
    return _.range(1, 1 + n);
  },

  async asyncForEach(data, callback) {
    await AsyncForEach(data, callback);
  },

  capitalizeString(value) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  },

  // currencyStringToInt converts a string value for money into an int.
  // Returns an integer or NaN.
  currencyStringToInt,

  // domainNameVerifier verifies a string representing a domain name.
  domainNameVerifier(domain) {
    // This used to use regex but that regex doesn't work when the domain
    // has more than 2 subdomains so just make it dumb.
    // return domain.match(domainRegex);
    return domain.indexOf('.') !== -1 && domain.indexOf('@') === -1;
  },

  // downloadFile requests and generate a one-time use token and then downloads
  // the file by creating a new window.
  async downloadFile(fileId) {
    try {
      const resp = await api.Get(api.urls.FILE_URL(fileId));
      const fileUrl = resp.data.url;
      const filename = resp.data.filename;
      this.downloadUrl(fileUrl, filename);
    } catch (err) {
      const errors = err.original.response.data.errors.join(', ');
      await this.$swal({
        title: 'File Download Error',
        text: errors,
        type: 'error',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showCancelButton: false,
        confirmButtonText: 'OK',
      });
    }
  },

  // downloadReport requests and generates a one-time use token and then
  // downloads the report using the downloadUrl utility function.
  async downloadReport(reportId, options, filter, returnUrlOnly, sendOptions) {
    const resp = await api.Post(
      api.urls.REPORT_CUSTOM_TOKEN,
      { reportId, options, filter, sendOptions },
    );
    const token = resp.data.token;
    const fileUrl = api.urls.REPORT_CUSTOM(token, options, filter);
    if (returnUrlOnly) return fileUrl;
    this.downloadUrl(fileUrl);
    return '';
  },

  // downloadURL downloads a URL without the need to create a new window.
  downloadUrl(url, filename) {
    let downloadName = '';
    if (filename) downloadName = filename;
    const link = document.createElement('a');
    link.setAttribute('download', downloadName);
    link.href = url;
    document.body.appendChild(link);
    link.click();
    link.remove();
  },

  emailRegex() {
    return emailRegex;
  },

  // emailVerifier verifies an email using the debounced version of the
  // verify API call. It also first checks to see if the data it gets is
  // an email; if it's not, it will not attempt to verify the email.
  //
  // It returns responses in the form required for combobox, that is,
  // an object of { msg: String, typ: String }.
  async emailVerifier(email, internalValue) {
    const emailVerifierD = Debounce(this.emailVerifierAPI, EmailDebounceTime);
    if (this.isEmailString(email) && internalValue.id === 'none' &&
      email.length > 2) {
      let resp;
      let userKnown;
      let needsVerification;
      try {
        resp = await emailVerifierD(email);
        userKnown = resp.data.exists;
        needsVerification = resp.data.needs_verification;
      } catch (err) {
        if (err.name === 'StatusCode422') return EmailVerifierMsgs.Failure;
        return EmailVerifierMsgs.NetworkFailure;
      }

      if (needsVerification) {
        return EmailVerifierMsgs.WarnNotInQreserve;
      }

      if (userKnown) {
        return EmailVerifierMsgs.SuccessInQReserve;
      }

      return EmailVerifierMsgs.WarnNotInQreserve;
    }

    // The ID is set, so we must have a real user for the site.
    if (internalValue.id !== 'none') {
      return EmailVerifierMsgs.SuccessInSite;
    }

    return EmailVerifierMsgs.Failure;
  },

  // emailVerifierAPI checks for whether or not an email exists in the
  // database through the backend API.
  async emailVerifierAPI(email) {
    const siteId = this.$route.params.siteId;
    const params = { test: true, email };
    const resp = await api.Post(api.urls.SITE_USER_ADD(siteId), params);

    if (resp) return resp;
    return null;
  },

  // externalStripeInit initializes the external Stripe JS scripts and should
  // only be done when a user could be requiring stripe and only if they accept
  // the cookies.
  externalStripeInit() {
    stripeInit(document, window);
    return true;
  },

  faIconsObj() {
    return faIcons;
  },

  // humanFileSize returns a human readable file size given a file size in
  // bytes.
  humanFileSize(bytes, si) {
    let bytesCopy = bytes;
    const thresh = si ? 1000 : 1024;
    if (Math.abs(bytes) < thresh) {
      return `${bytes} B`;
    }
    const units = si
      ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
      : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
    let u = -1;
    do {
      bytesCopy /= thresh;
      u += 1;
    } while (Math.abs(bytesCopy) >= thresh && u < units.length - 1);
    return `${bytesCopy.toFixed(1)} ${units[u]}`;
  },

  // integerMoneyToNumber converts an integer for money into a Number value.
  integerMoneyToNumber(int) {
    if (_.isNull(int)) return null;
    return _.round(int / 100, 2);
  },

  // A convenience function to check whether a role is an administrator role.
  isAdministratorRole(role) {
    const roles = ['administrator'];
    return roles.indexOf(role) !== -1;
  },

  // isEmailString checks to see if a string is an email address.
  isEmailString(str) {
    return emailRegex.test(str);
  },

  // A convenience function to check whether a role is a moderator role.
  isModeratorRole(role) {
    const roles = ['moderator', 'administrator'];
    return roles.indexOf(role) !== -1;
  },

  // isPromise checks to see if a value is a Promise or not. See:
  // stackoverflow.com/questions/27746304/
  //   how-do-i-tell-if-an-object-is-a-promise
  isPromise(maybePromise) {
    return _.get(maybePromise, 'then', false) &&
      _.isFunction(maybePromise.then);
  },

  // isSameObject returns a boolean indicating whether or not an object is
  // the same.
  isSameObject(a, b) {
    return JSON.stringify(a) === JSON.stringify(b);
  },

  // numberToIntegerMoney converts a number value for money to an integer value.
  numberToIntegerMoney(num) {
    if (_.isNull(num)) return null;
    return _.round(num * 100, 0);
  },

  // popperId is the slot element ID for the pop-up element.
  popperId(id, id2, id3) {
    if (String(id2) && !id3) return `slot-${String(id)}-${String(id2)}`;
    if (String(id2) && String(id3)) {
      return `slot-${String(id)}-${String(id2)}-${String(id3)}`;
    }
    return `slot-${String(id)}`;
  },

  // priceFmt returns a formatted price string.
  // extraData allows for customisation of the string:
  // extraData: {
  //   currency: Current string prefix (e.g. '$').
  //   currency_thousand: Location of thousands separator.
  //   currency_decimal: Location of decimal separator.
  //   currency_code: Currency code to find in list of currencies.
  //   isInteger: The value is expressed as an integer and must be multiplied
  //     by the currency's minor value to get the actual value.
  //   showCurrencyCode: Also include the currency code before the symbol.
  // }
  priceFmt(price, extraData) {
    if (price === undefined) return '';
    let value = Number(price).toFixed(2);
    if (extraData) {
      // Tweak the value if we're using an integer and the currency's base
      // unit.
      // TODO Test this
      if (extraData.isInteger && extraData.currency_code) {
        const currency = _.find(CURRENCIES, (c) =>
          _.get(c, 'iso.code') === extraData.currency_code);
        if (currency && _.get(currency, 'units.minor.majorValue')) {
          value *= _.get(currency, 'units.minor.majorValue');
        }
      }

      let prefix = '';
      if (extraData.currency) prefix = extraData.currency;
      if (extraData.showCurrencyCode && extraData.currency_code) {
        prefix = `${extraData.currency_code} ${prefix}`;
      }
      let thousand = ',';
      if (extraData.currency_thousand) thousand = extraData.currency_thousand;
      let divider = '.';
      if (extraData.currency_decimal) divider = extraData.currency_decimal;
      // TODO Not sure if this needs to be displayed?
      // let code = '';
      // if (extraData.currency_code) code = extraData.currency_code;
      const valueStr = Number(value).toFixed(2);
      const parts = valueStr.split('.');
      let isNegative = false;
      if (_.get(parts, '[0][0]') === '-') {
        isNegative = true;
        parts[0] = parts[0].slice(1);
      }

      // Add dividers and prefix.
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousand);
      parts[0] = `${isNegative ? '-' : ''}${prefix.trim()}${parts[0]}`;
      // TODO Not sure if this needs to be displayed?
      // if (code) parts[1] = `${parts[1]} ${code.trim()}`;

      if (extraData.noCents) return parts[0];
      return parts.join(divider);
    }
    return value;
  },

  // queryParamMaybeStringToBoolean converts a string query parameter value into
  // a boolean value. Can also handle undefined, null, or Boolean values.
  queryParamMaybeStringToBoolean(str) {
    if (_.isBoolean(str)) return str;
    if (_.isNil(str)) return false;
    if (_.includes(['t', 'T', '1'], str.charAt(0))) return true;
    if (_.includes(['f', 'F', '0'], str.charAt(0))) return false;
    return false;
  },

  // resetStore resets the application vuex store to an empty state, except for
  // themes and the license.
  resetStore() {
    this.$store.dispatch('FilterReset',
      { namespace: 'adminActualUsageFilterState' });
    this.$store.dispatch('FilterReset',
      { namespace: 'adminInvoiceFilterState' });
    this.$store.dispatch('FilterReset',
      { namespace: 'adminReservablesFilterState' });
    this.$store.dispatch('FilterReset',
      { namespace: 'adminSubscriptionFilterState' });
    this.$store.dispatch('FilterReset',
      { namespace: 'reservationFilterState' });
    this.$store.dispatch('FilterResetNonDefaultNamespaces');

    this.$store.dispatch('AuthDataReset');
    this.$store.dispatch('KeyStoreReset');
    this.$store.dispatch('MembershipsReset');
    this.$store.dispatch('PortalsReset');
    this.$store.dispatch('ReservableEditReset');
    this.$store.dispatch('SearchReset');
    this.$store.dispatch('SiteSearchReset');
    this.$store.dispatch('SiteSearchReset');
    this.$store.dispatch('ClearFormClipboard');
    this.$store.dispatch('ClearSessionSettings');
  },

  // sanitizeHTML sanitizes user input HTML, stripping out malicious tags like
  // <script>.
  sanitizeHTML(val, options) {
    let result = SanitizeHTML(val);

    // Apply any additional options.
    if (options && options.allowAmp) {
      result = result.replace('&amp;', '&');
    }

    return result;
  },

  // setTitle sets the browser title and prefixes it appropriately.
  setTitle(title) {
    if (!title) {
      document.title = 'QReserve';
    } else {
      document.title = `${title} | QReserve`;
    }
  },

  // shipNotification displays a toast notification. Valid types are:
  // info, success, warning, danger
  // If an unknown type is passed, an error is thrown.
  shipNotification(txt, typ, dismiss) {
    // No notifications in testing.
    const localEnv = CI_TESTING ? 'testing' : process.env.NODE_ENV;
    if (localEnv === 'testing') return;

    let chosenType = 'info';
    if (typ) {
      if (!_.includes(['info', 'success', 'warning', 'danger'], typ)) {
        throw Error(`Unknown toast type ${typ}`);
      }
      chosenType = typ;
    }

    const txtSane = this.sanitizeHTML(txt);
    let icon;
    const cssIconStyle = `
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      position: absolute;
    `;
    switch (typ) {
      case 'info':
        icon = this.faSVG(faIcons.faInfoCircle, '', cssIconStyle);
        break;
      case 'success':
        icon = this.faSVG(faIcons.faCheck, '', cssIconStyle);
        break;
      case 'warning':
        icon = this.faSVG(faIcons.faExclamationTriangle, '', cssIconStyle);
        break;
      case 'danger':
        icon = this.faSVG(faIcons.faExclamationTriangleSolid, '', cssIconStyle);
        break;
      default:
        break;
    }
    const containerStyle = `
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      justify-content: space-between;
    `;
    const iconStyle = `
      width: 25px;
      height: 25px;
      margin: 0 auto;
      position: relative;
    `;
    const iconContainerDivStyle = `
      padding: 8px 16px 8px 8px;
    `;
    const txtStyle = `
      height: 100%;
      display: flex;
      justify-content: center;
      align-content: center;
      flex-direction: column;
    `;
    const params = {
      text: `<div style="${containerStyle}">
        <div style="${iconContainerDivStyle}">
          <div style="${iconStyle}">${icon}</div>
        </div>
        <div style="width: 100%">
          <div style="${txtStyle}">${txtSane}</div>
        </div>
      </div>`,
    };
    params.type = chosenType;
    if (dismiss) params.dismissAfter = dismiss;
    this.$store.dispatch(ADD_TOAST_MESSAGE, params);
  },

  // signOut signs the user out and resets the state.
  async signOut({
    allSessions = false,
    alreadyLoggedOut = false,
    callbackFn = null,
  } = {}) {
    const opts = { params: { all_sessions: allSessions } };

    let defaultSSO;
    try {
      defaultSSO = window.localStorage.getItem(SSO.DefaultEntityKey);
    } catch (err) {
      // It might not exist.
    }

    if (defaultSSO) {
      try {
        const idpObj = JSON.parse(defaultSSO);
        opts.params.idp_entity_id = idpObj.entity_id;
      } catch (err) {
        console.warn('Failed to deserialize IDP entity ID from localStorage',
          err);
      }
    }

    this.wsTerminate();
    await api.WaitForAllApiCallsToFinish();

    let resp;
    let successfulLogout = false;
    if (!alreadyLoggedOut && this.$store.state.auth?.authUser) {
      try {
        resp = await api.Get(api.urls.LOGOUT, opts);
        successfulLogout = true;
        window.stop();
      } catch (err) {
        console.warn('Failed to execute logout API call:', err);
      }
    }

    let redirectorURL = '';
    if (_.get(resp, 'data.redirector')) {
      redirectorURL = resp.data.redirector;
    }

    // Kill global auto logout timer.
    window.clearTimeout(_.get(window, 'autoInactivityLogoutThread'));

    if (successfulLogout) {
      auth.Logout(this.$router, redirectorURL);
    } else if (auth.TokenFetch()) {
      auth.TokenPurge();
    }

    this.resetStore();

    if (!this.successfulLogout) {
      this.$router.Navigate.Login();
    } else {
      auth.StayLoggedInStore(false);
    }

    if (_.isFunction(callbackFn)) callbackFn();
  },

  // sleep is a general use function to create a sleeping promise.
  sleep(ms) {
    /* eslint-disable-next-line */
    return new Promise((resolve) => setTimeout(resolve, ms));
  },

  // sortByDisplayNameFunc is a sort function used to case insensitively
  // sort an array of user objects by their display_name property.
  sortByDisplayNameFunc(a, b) {
    const nA = a.display_name.toLowerCase();
    const nB = b.display_name.toLowerCase();
    if (nA < nB) return -1;
    if (nA > nB) return 1;
    return 0;
  },

  // sortByEmailFunc is a sort function used to case insensitively sort an
  // array of objects by their email property.
  sortByEmailFunc(a, b) {
    const nA = a.email.toLowerCase();
    const nB = b.email.toLowerCase();
    if (nA < nB) return -1;
    if (nA > nB) return 1;
    return 0;
  },

  // sortByFieldNameFunc is a sort function used to case insensitively sort an
  // array of objects by the passed field name property.
  sortByFieldNameFunc(fieldName) {
    return (a, b) => {
      let nA = a[fieldName];
      let nB = b[fieldName];
      if (_.isString(a[fieldName])) nA = a[fieldName].toLowerCase();
      if (_.isString(b[fieldName])) nB = b[fieldName].toLowerCase();
      if (nA < nB) return -1;
      if (nA > nB) return 1;
      return 0;
    };
  },

  // sortByNameFunc is a sort function used to case insensitively sort an
  // array of objects by their name property.
  sortByNameFunc(a, b) {
    const nA = a.name.toLowerCase();
    const nB = b.name.toLowerCase();
    if (nA < nB) return -1;
    if (nA > nB) return 1;
    return 0;
  },

  // sortCaseInsensitive is a case insensitive sort function.
  sortCaseInsensitive(a, b) {
    const nA = a.toLowerCase();
    const nB = b.toLowerCase();
    if (nA < nB) return -1;
    if (nA > nB) return 1;
    return 0;
  },

  // stackTrace prints a stacktrace from the current location. Useful in
  // debugging.
  stackTrace() {
    const stack = new Error().stack;
    console.debug(stack);
  },

  // staticScriptAsyncLoad returns a promise that loads a script from the
  // static folder or a remote path asyncronously into window[name].
  staticScriptAsyncLoad({
    name = null,
    urlPathLocal = null,
    urlPathRemote = null,
  }) {
    let url;
    if (urlPathLocal) url = `${STATIC_URL}/${urlPathLocal}`;
    if (urlPathRemote) url = urlPathRemote;

    return new Promise((resolve) => {
      if (window[name]) {
        resolve(window[name]);
        return;
      }
      const script = document.createElement('script');
      script.async = true;
      document.body.appendChild(script);
      script.addEventListener('load', () => {
        if (!window[name]) window[name] = true;
        resolve(window[name]);
      });
      script.addEventListener('error', (err) => {
        throw err;
      });
      script.src = url;
    });
  },

  // uuid4 makes a random uuid.
  uuid4() {
    return uuid4();
  },

  async waitForRefToLoad(name) {
    for (let i = 0; i < waitForRefToLoadMaxIterations; i += 1) {
      const ref = this.$refs[name];
      if (ref) return;
      await this.sleep(waitForRefToLoadDelay);
    }
    console.warn(`ref failed to be found in parent: '${name}'`);
  },

  // whatHolidayIsIt returns a lowercase string for today's holiday (if any).
  whatHolidayIsIt() {
    const d = moment();
    if (d.date() === 31 && d.month() === 9) return 'halloween';
    if (d.date() >= 11 && d.date() <= 23 && d.month() === 11) {
      return 'prechristmas';
    }
    if (d.date() === 24 && d.month() === 11) return 'christmaseve';
    if (d.date() === 25 && d.month() === 11) return 'christmas';
    if (d.date() === 26 && d.month() === 11) return 'boxingday';
    if (d.date() === 31 && d.month() === 11) return 'nye';
    return 'a regular day';
  },

  // writeQueryParametersToURL is a generic function that lets you write query
  // parameters to a URL.
  //
  // @toWrite: Object containing the list of values you wish to write to the
  //   URL parameters.
  // @validQueryParamKeys: A list of parameter keys which we should not
  //  copy from the current query parameters into our new URL query parameters.
  async writeQueryParametersToURL({
    replace = false,
    toDelete = null,
    toWrite = null,
    validQueryParamKeys = [],
  }) {
    if (!_.isPlainObject(toWrite)) throw Error('toWrite must be object');
    const toWriteCopy = _.cloneDeep(toWrite);

    // Set any other peripheral URL parameters for this page, which are
    // unrelated to the filter widget.
    _.forEach(this.$route.query, (val, key) => {
      if (!_.has(toWriteCopy, key) && !_.includes(validQueryParamKeys, key)) {
        _.set(toWriteCopy, key, val);
      }
    });

    // Delete any requested keys.
    if (_.isArray(toDelete)) {
      _.forEach(toDelete, (key) => _.unset(toWriteCopy, key));
    }

    // Only write an update if there is a change.
    const theSame =
      _.differenceWith(
        _.sortBy(_.toPairs(this.$route.query), (pair) => pair[0]),
        _.sortBy(_.toPairs(toWriteCopy), (pair) => pair[0]),
        _.isEqual) === [] &&
      _.differenceWith(
        _.sortBy(_.toPairs(toWriteCopy), (pair) => pair[0]),
        _.sortBy(_.toPairs(this.$route.query), (pair) => pair[0]),
        _.isEqual) === [];
    if (theSame) {
      return;
    }

    if (!replace) {
      await this.$router.push({
        query: toWriteCopy,
      });
    } else {
      await this.$router.replace({
        query: toWriteCopy,
      });
    }
  },

  // wsChannel returns the main websocket channel.
  async wsChannel() {
    await ctx.channelSet;
    return ctx.mainChContainer.mainCh;
  },

  // wsConnect connects to the websocket and spawns the main global channel.
  wsConnect(jwt, process) {
    const ch = ctx.assignMainCh(jwt, process, this.shipNotification);
    ctx.channelSetter();
    return ch;
  },

  // wsTerminate kills the websocket and the main channel.
  async wsTerminate() {
    const websocketIsKill = _.get(ctx,
      'mainChContainer.websocket.closingPromise');
    if (ctx.mainChContainer.mainCh &&
      !ctx.mainChContainer.mainCh.isClosed()) {
      try {
        putAsync(ctx.mainChContainer.mainCh, 'kill');
        if (websocketIsKill) await websocketIsKill;
      } catch (err) {
        console.warn('failed to close main websocket channel: ' +
          `${err}`);
      }
    }
  },
});
